import { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, Plus, Search, Eye, Pencil, Trash2, X, Check, ChevronLeft, ChevronRight, UserCheck, Copy, ExternalLink, Clock } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { supabase, Influencer } from '../lib/supabase';
import InfluencerAvatar from '../components/InfluencerAvatar';
import { useAuth } from '../contexts/AuthContextSupabase';

interface DailyBrief {
  id?: string;
  agency_id: string;
  agency_name: string;
  influencer_id: string;
  influencer_name: string;
  influencer_slug: string;
  date: string;
  tasks: Array<{ id: string; text: string; completed: boolean }>;
  internal_notes: string;
  created_at?: string;
  updated_at?: string;
}

const formatDateToLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function DailyBriefPage() {
  const { userProfile } = useAuth();
  const [briefs, setBriefs] = useState<DailyBrief[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isSearchingInfluencers, setIsSearchingInfluencers] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState<DailyBrief | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isDayDetailOpen, setIsDayDetailOpen] = useState(false);
  const [newTask, setNewTask] = useState('');
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [isInfluencerSelectorOpen, setIsInfluencerSelectorOpen] = useState(false);
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [formData, setFormData] = useState<Partial<DailyBrief>>({
    agency_id: '',
    agency_name: '',
    influencer_id: '',
    influencer_name: '',
    influencer_slug: '',
    date: formatDateToLocal(new Date()),
    tasks: [],
    internal_notes: '',
  });

  useEffect(() => {
    if (userProfile?.agency_id) {
      loadData();
    }
  }, [userProfile?.agency_id]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const { data: briefsData, error: briefsError } = await supabase
        .from('daily_briefs')
        .select('*')
        .eq('agency_id', userProfile?.agency_id || '')
        .order('date', { ascending: true });

      if (briefsError) {
        console.error('Error loading briefs:', briefsError);
        setBriefs([]);
      } else {
        setBriefs(briefsData || []);

        const influencerIds = [...new Set(briefsData?.map(b => b.influencer_id).filter(Boolean) || [])];

        if (influencerIds.length > 0) {
          const { data: influencersData, error: influencersError } = await supabase
            .from('influencers')
            .select('id, display_name, full_name, username, profile_image_url, followers_count')
            .in('id', influencerIds);

          if (influencersError) {
            console.error('Error loading influencers:', influencersError);
          } else {
            setInfluencers(influencersData || []);
          }
        }
      }

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const searchInfluencers = useCallback(async (searchTerm: string) => {
    try {
      setIsSearchingInfluencers(true);

      let query = supabase
        .from('influencers')
        .select('id, display_name, full_name, username, profile_image_url, followers_count')
        .eq('archived', false)
        .limit(50);

      if (searchTerm.trim()) {
        query = query.or(`full_name.ilike.%${searchTerm}%,display_name.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`);
      } else {
        query = query.order('followers_count', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error searching influencers:', error);
        setInfluencers([]);
      } else {
        setInfluencers(data || []);
      }
    } catch (error) {
      console.error('Error searching influencers:', error);
      setInfluencers([]);
    } finally {
      setIsSearchingInfluencers(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (isInfluencerSelectorOpen) {
        searchInfluencers(influencerSearch);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [influencerSearch, isInfluencerSelectorOpen, searchInfluencers]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!userProfile?.agency_id) {
        throw new Error('Aucune agence associée');
      }

      const selectedInf = influencers.find(inf => inf.id === formData.influencer_id);
      if (!selectedInf) {
        alert('Veuillez sélectionner un influenceur');
        return;
      }

      const tasksToSave = [...(formData.tasks || [])];
      if (newTask.trim()) {
        tasksToSave.push({
          id: Date.now().toString(),
          text: newTask.trim(),
          completed: false,
        });
      }

      if (tasksToSave.length === 0) {
        alert('Veuillez ajouter au moins une tâche');
        return;
      }

      const { data: agencyData } = await supabase
        .from('agencies')
        .select('name')
        .eq('id', userProfile.agency_id)
        .single();

      const influencerSlug = generateSlug(selectedInf.full_name || selectedInf.display_name || 'influenceur');

      const { data: existingBrief } = await supabase
        .from('daily_briefs')
        .select('*')
        .eq('agency_id', userProfile.agency_id)
        .eq('influencer_slug', influencerSlug)
        .eq('date', formData.date!)
        .maybeSingle();

      const briefData = {
        agency_id: userProfile.agency_id,
        agency_name: agencyData?.name || 'Campaly',
        influencer_id: formData.influencer_id!,
        influencer_name: selectedInf.full_name || selectedInf.display_name || 'Influenceur',
        influencer_slug: influencerSlug,
        date: formData.date!,
        tasks: tasksToSave,
        internal_notes: formData.internal_notes || '',
        updated_at: new Date().toISOString(),
      };

      if (selectedBrief?.id) {
        const { error } = await supabase
          .from('daily_briefs')
          .update(briefData)
          .eq('id', selectedBrief.id);

        if (error) throw error;
      } else if (existingBrief) {
        const mergedTasks = [...(existingBrief.tasks || []), ...tasksToSave];
        const { error } = await supabase
          .from('daily_briefs')
          .update({
            tasks: mergedTasks,
            internal_notes: formData.internal_notes || existingBrief.internal_notes || '',
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingBrief.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('daily_briefs')
          .insert({
            ...briefData,
            created_at: new Date().toISOString(),
          });

        if (error) throw error;
      }

      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving brief:', error);
      alert('Erreur lors de l\'enregistrement de la tâche');
    }
  };

  const handleDelete = async (briefId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette tâche ?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('daily_briefs')
        .delete()
        .eq('id', briefId);

      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error deleting brief:', error);
      alert('Erreur lors de la suppression de la tâche');
    }
  };

  const handleEdit = (brief: DailyBrief) => {
    setSelectedBrief(brief);
    setFormData({
      agency_id: brief.agency_id,
      agency_name: brief.agency_name,
      influencer_id: brief.influencer_id,
      influencer_name: brief.influencer_name,
      influencer_slug: brief.influencer_slug,
      date: brief.date,
      tasks: [...brief.tasks],
      internal_notes: brief.internal_notes,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBrief(null);
    setNewTask('');
    setInfluencerSearch('');
    setFormData({
      agency_id: '',
      agency_name: '',
      influencer_id: '',
      influencer_name: '',
      influencer_slug: '',
      date: formatDateToLocal(new Date()),
      tasks: [],
      internal_notes: '',
    });
  };

  const handleSelectInfluencer = (influencer: Influencer) => {
    const influencerName = influencer.full_name || influencer.display_name || influencer.username;
    const influencerSlug = influencerName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    setFormData({
      ...formData,
      influencer_id: influencer.id,
      influencer_name: influencerName,
      influencer_slug: influencerSlug,
    });
    setIsInfluencerSelectorOpen(false);
    setInfluencerSearch('');
  };

  const handleOpenInfluencerSelector = () => {
    setInfluencerSearch('');
    setIsInfluencerSelectorOpen(true);
  };

  const handleAddTask = () => {
    if (newTask.trim()) {
      const task = {
        id: Date.now().toString(),
        text: newTask.trim(),
        completed: false,
      };
      setFormData(prev => ({
        ...prev,
        tasks: [...(prev.tasks || []), task]
      }));
      setNewTask('');
    }
  };

  const handleRemoveTask = (taskId: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks?.filter(task => task.id !== taskId) || []
    }));
  };

  const handleTaskChange = (taskId: string, newText: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks?.map(task => 
        task.id === taskId ? { ...task, text: newText } : task
      ) || []
    }));
  };

  const copyInfluencerLink = async (brief: DailyBrief) => {
    const link = `${window.location.origin}/brief/${brief.influencer_slug}`;
    await navigator.clipboard.writeText(link);
    setCopiedLink(brief.id!);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const formatDate = (date: string | Date | undefined) => {
    if (!date) return 'Date inconnue';

    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    for (let i = 0; i < (startDayOfWeek === 0 ? 6 : startDayOfWeek - 1); i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getBriefsForDate = (date: Date) => {
    const dateStr = formatDateToLocal(date);
    return briefs.filter(brief => {
      const briefDate = brief.date ? new Date(brief.date).toISOString().split('T')[0] : '';
      return briefDate === dateStr;
    });
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleDayClick = (date: Date) => {
    const dateStr = formatDateToLocal(date);
    const dayBriefs = getBriefsForDate(date);
    if (dayBriefs.length > 0) {
      setSelectedDate(dateStr);
      setIsDayDetailOpen(true);
    }
  };

  const formatDateInput = (date: string | Date | undefined) => {
    if (!date) return '';

    return new Date(date).toISOString().split('T')[0];
  };


  const selectedInfluencer = influencers.find(inf => inf.id === formData.influencer_id);

  const monthDays = getDaysInMonth(currentDate);
  const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const selectedDateBriefs = selectedDate ? briefs.filter(b => {
    const briefDate = b.date ? new Date(b.date).toISOString().split('T')[0] : '';
    return briefDate === selectedDate;
  }) : [];

  const totalTasks = briefs.reduce((acc, brief) => acc + brief.tasks.length, 0);
  const completedTasks = briefs.reduce((acc, brief) => acc + brief.tasks.filter(t => t.completed).length, 0);
  const activeInfluencers = new Set(briefs.map(b => b.influencer_id)).size;
  const todayBriefs = briefs.filter(b => {
    const today = formatDateToLocal(new Date());
    const briefDate = b.date ? new Date(b.date).toISOString().split('T')[0] : '';
    return briefDate === today;
  });

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center shadow-lg">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Tâche influenceur</h1>
            <p className="text-sm text-white/50">Planning mensuel de vos influenceurs</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Stats rapides */}
          <div className="hidden lg:flex items-center gap-4 px-4 py-2 bg-white/[0.04] rounded-lg border border-white/[0.08]">
            <div className="text-center">
              <div className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Aujourd'hui</div>
              <div className="text-xl font-bold text-white">{todayBriefs.length}</div>
            </div>
            <div className="w-px h-8 bg-white/[0.08]" />
            <div className="text-center">
              <div className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Influenceurs</div>
              <div className="text-xl font-bold text-white">{activeInfluencers}</div>
            </div>
            <div className="w-px h-8 bg-white/[0.08]" />
            <div className="text-center">
              <div className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Progression</div>
              <div className="text-xl font-bold text-white">{totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%</div>
            </div>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-black font-semibold rounded-xl hover:bg-white/90 transition-all hover:shadow-lg hover:scale-105"
          >
            <Plus className="w-5 h-5" />
            Nouvelle tâche
          </button>
        </div>
      </div>

      {/* Calendar Card - Full height */}
      <div className="flex-1 bg-white/[0.04] border-white/[0.08] rounded-xl border flex flex-col overflow-hidden">
        {/* Navigation mois - Fixed header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/[0.08]">
          <h2 className="text-3xl font-bold text-white capitalize">
            {getMonthName(currentDate)}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={goToPreviousMonth}
              className="p-3 text-white/50 hover:text-white hover:bg-white/[0.08] rounded-lg transition-all hover:scale-110"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentDate(new Date())}
              className="px-4 py-2 text-sm font-medium text-white/60 bg-white/[0.08] hover:bg-white/[0.14] rounded-xl transition-colors"
            >
              Aujourd'hui
            </button>
            <button
              onClick={goToNextMonth}
              className="p-3 text-white/50 hover:text-white hover:bg-white/[0.08] rounded-lg transition-all hover:scale-110"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-white/10 border-t-white/60"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 pt-4">
            {/* En-têtes des jours de la semaine */}
            <div className="grid grid-cols-7 gap-3 mb-3 sticky top-0 bg-transparent z-10 pb-2">
              {weekdayLabels.map((day) => (
                <div key={day} className="text-center text-[10px] font-semibold text-white/40 uppercase tracking-widest py-3 bg-white/[0.04] rounded-lg">
                  {day}
                </div>
              ))}
            </div>

            {/* Grille du calendrier */}
            <div className="grid grid-cols-7 gap-3 auto-rows-fr">
              {monthDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} />;
                }

                const dayBriefs = getBriefsForDate(date);
                const isToday = date.toDateString() === new Date().toDateString();
                const hasTasks = dayBriefs.length > 0;
                const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                const dayCompletedTasks = dayBriefs.reduce((acc, b) => acc + b.tasks.filter(t => t.completed).length, 0);
                const dayTotalTasks = dayBriefs.reduce((acc, b) => acc + b.tasks.length, 0);
                const completionRate = dayTotalTasks > 0 ? (dayCompletedTasks / dayTotalTasks) * 100 : 0;

                return (
                  <div
                    key={index}
                    onClick={() => hasTasks && handleDayClick(date)}
                    className={`
                      relative rounded-xl p-3 transition-all duration-300 min-h-[120px] flex flex-col
                      ${hasTasks
                        ? 'cursor-pointer bg-white/[0.06] border border-white/[0.08] hover:border-white/30 hover:shadow-xl hover:scale-105 hover:-translate-y-1'
                        : 'bg-white/[0.02] border border-white/[0.04]'
                      }
                      ${isToday ? 'border-white/40 border-2 shadow-lg ring-2 ring-white/10' : ''}
                      ${isPast && !isToday ? 'opacity-60' : ''}
                    `}
                  >
                    {/* Date badge */}
                    <div className="flex items-center justify-between mb-2">
                      <div className={`
                        text-lg font-bold flex items-center justify-center rounded-lg px-2 py-1
                        ${isToday ? 'bg-white text-black' : hasTasks ? 'text-white' : 'text-white/30'}
                      `}>
                        {date.getDate()}
                      </div>
                      {hasTasks && (
                        <div className="flex items-center gap-1">
                          <div className="text-xs font-medium text-white/40">
                            {dayBriefs.length}
                          </div>
                        </div>
                      )}
                    </div>

                    {hasTasks && (
                      <div className="flex-1 flex flex-col gap-2">
                        {/* Progression bar */}
                        {dayTotalTasks > 0 && (
                          <div className="w-full bg-white/[0.08] rounded-full h-1.5 overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 ${
                                completionRate === 100 ? 'bg-green-500' : 'bg-white/50'
                              }`}
                              style={{ width: `${completionRate}%` }}
                            />
                          </div>
                        )}

                        {/* Avatars influenceurs */}
                        <div className="flex flex-wrap gap-1.5">
                          {dayBriefs.slice(0, 4).map((brief) => {
                            const influencer = influencers.find(
                              inf => inf.id === brief.influencer_id
                            );
                            const completedTasks = brief.tasks.filter(t => t.completed).length;
                            const totalTasks = brief.tasks.length;
                            const isComplete = completedTasks === totalTasks;

                            return (
                              <div key={brief.id} className="relative group/avatar">
                                <div className={`rounded-full transition-all ${isComplete ? 'ring-2 ring-green-400' : ''}`}>
                                  <InfluencerAvatar
                                    profileImageUrl={influencer?.profile_image_url || null}
                                    name={brief.influencer_name}
                                    size="sm"
                                  />
                                </div>
                                {/* Badge tâches */}
                                <div className={`
                                  absolute -bottom-1 -right-1
                                  ${isComplete ? 'bg-green-500' : 'bg-white/[0.08]'}
                                  border border-white/20 rounded-full text-[9px] px-1.5 py-0.5 font-bold
                                  ${isComplete ? 'text-white' : 'text-white/70'}
                                  shadow-sm
                                `}>
                                  {completedTasks}/{totalTasks}
                                </div>
                                {/* Tooltip au hover */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover/avatar:opacity-100 transition-opacity pointer-events-none z-10">
                                  <div className="bg-black/80 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                                    {brief.influencer_name}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          {dayBriefs.length > 4 && (
                            <div className="w-8 h-8 rounded-full bg-white/[0.12] flex items-center justify-center text-[10px] font-bold text-white/60 shadow-sm">
                              +{dayBriefs.length - 4}
                            </div>
                          )}
                        </div>

                        {/* Indicateur de complétion */}
                        {completionRate === 100 && (
                          <div className="flex items-center gap-1 text-green-400 text-xs font-medium mt-auto">
                            <Check className="w-3 h-3" />
                            <span>Terminé</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal détail du jour */}
      <Dialog
        open={isDayDetailOpen}
        onClose={() => setIsDayDetailOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsDayDetailOpen(false)}
                className="text-white/40 hover:text-white/70"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className="text-2xl font-bold text-white mb-6">
              {selectedDate && formatDate(selectedDate)}
            </h2>

            <div className="space-y-6">
              {selectedDateBriefs.map((brief) => {
                const influencer = influencers.find(
                  inf => inf.id === brief.influencer_id
                );

                return (
                  <div key={brief.id} className="border border-white/[0.08] rounded-xl p-4">
                    {/* Header influenceur */}
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/[0.08]">
                      <div className="flex items-center gap-3">
                        <InfluencerAvatar
                          profileImageUrl={influencer?.profile_image_url || null}
                          name={brief.influencer_name}
                          size="md"
                        />
                        <div>
                          <h3 className="font-semibold text-white">{brief.influencer_name}</h3>
                          <p className="text-sm text-white/50">
                            {brief.tasks.filter(t => t.completed).length}/{brief.tasks.length} terminées
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyInfluencerLink(brief)}
                          className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
                          title="Copier le lien"
                        >
                          {copiedLink === brief.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        <a
                          href={`/brief/${brief.influencer_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
                          title="Voir la page publique"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleEdit(brief)}
                          className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(brief.id!)}
                          className="p-2 text-white/40 hover:text-red-400 hover:bg-white/[0.08] rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Liste des tâches */}
                    <div className="space-y-2">
                      {brief.tasks.map((task) => (
                        <div
                          key={task.id}
                          className={`flex items-start gap-2 p-2 rounded-lg ${
                            task.completed ? 'bg-green-500/10' : 'bg-white/[0.04]'
                          }`}
                        >
                          <div className={`w-4 h-4 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            task.completed
                              ? 'bg-green-500 border-green-500'
                              : 'border-white/20'
                          }`}>
                            {task.completed && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className={`flex-1 text-sm ${
                            task.completed ? 'line-through text-white/30' : 'text-white'
                          }`}>
                            {task.text}
                          </span>
                        </div>
                      ))}
                    </div>

                    {brief.internal_notes && (
                      <div className="mt-4 pt-3 border-t border-white/[0.08]">
                        <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-1">Notes internes:</p>
                        <p className="text-sm text-white/50">{brief.internal_notes}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => {
          if (isInfluencerSelectorOpen) {
            return;
          }
          if (confirm('Voulez-vous vraiment annuler ? Les modifications non enregistrées seront perdues.')) {
            handleCloseModal();
          }
        }}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-white/40 hover:text-white/70"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">
                  {selectedBrief ? 'Modifier la tâche' : 'Nouvelle tâche influenceur'}
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white/60 mb-2">
                        Influenceur <span className="text-red-400">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={handleOpenInfluencerSelector}
                        className={`w-full flex items-center justify-between p-3 rounded-xl transition-colors text-left bg-white/[0.06] border hover:bg-white/[0.08] ${
                          !formData.influencer_id ? 'border-white/[0.08]' : 'border-white/20'
                        }`}
                      >
                        {selectedInfluencer ? (
                          <div className="flex items-center gap-2">
                            <InfluencerAvatar
                              profileImageUrl={selectedInfluencer.profile_image_url}
                              name={selectedInfluencer.name || selectedInfluencer.display_name || selectedInfluencer.username}
                              size="sm"
                            />
                            <div>
                              <div className="font-medium text-white">
                                {selectedInfluencer.name || selectedInfluencer.display_name}
                              </div>
                              <div className="text-sm text-white/50">
                                @{selectedInfluencer.username}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-white/30">Sélectionner un influenceur</span>
                        )}
                        <Search className="w-5 h-5 text-white/30" />
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white/60 mb-2">
                        Date <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white focus:ring-1 focus:ring-white/20 outline-none"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/60 mb-2">
                      Tâches
                    </label>
                    <div className="space-y-3">
                      {formData.tasks?.map((task) => (
                        <div key={task.id} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={task.text}
                            onChange={(e) => handleTaskChange(task.id, e.target.value)}
                            className="flex-1 bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
                            placeholder="Description de la tâche"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(task.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTask}
                          onChange={(e) => setNewTask(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddTask();
                            }
                          }}
                          className="flex-1 bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
                          placeholder="Nouvelle tâche..."
                        />
                        <button
                          type="button"
                          onClick={handleAddTask}
                          className="px-3 py-2 bg-white/[0.08] text-white/60 rounded-xl hover:bg-white/[0.14] border border-white/[0.08]"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/60">
                      Notes internes (cachées pour l'influenceur)
                    </label>
                    <textarea
                      value={formData.internal_notes}
                      onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
                      placeholder="Notes visibles uniquement par l'équipe..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90"
                  >
                    {selectedBrief ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Influencer Selector Modal */}
      <Dialog
        open={isInfluencerSelectorOpen}
        onClose={() => setIsInfluencerSelectorOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsInfluencerSelectorOpen(false)}
                className="p-2 rounded-lg bg-white/[0.08] text-white/60 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white">
                Sélectionner un influenceur
              </h2>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  value={influencerSearch}
                  onChange={(e) => setInfluencerSearch(e.target.value)}
                  placeholder="Rechercher un influenceur..."
                  className="w-full pl-10 pr-4 py-2 bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none"
                />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {isSearchingInfluencers && (
                  <div className="text-center py-8 text-white/40">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/10 border-t-white/60 mx-auto mb-3"></div>
                    <p>Recherche en cours...</p>
                  </div>
                )}

                {!isSearchingInfluencers && influencers.map((influencer) => (
                  <div
                    key={influencer.id}
                    onClick={() => handleSelectInfluencer(influencer)}
                    className="w-full flex items-center p-4 bg-white/[0.04] hover:bg-white/[0.08] rounded-xl transition-colors cursor-pointer"
                  >
                    <InfluencerAvatar
                      profileImageUrl={influencer.profile_image_url}
                      name={influencer.full_name || influencer.display_name || influencer.username}
                      size="md"
                      className="mr-3"
                    />
                    <div className="text-left">
                      <h3 className="font-medium text-white">
                        {influencer.full_name || influencer.display_name}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-white/50">
                        <span>@{influencer.username}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {!isSearchingInfluencers && influencers.length === 0 && (
                  <div className="text-center py-8 text-white/30">
                    <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun influenceur trouvé</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
